import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoanDocumentUploadModal from '../components/loans/LoanDocumentUploadModal';
import { api } from '../api/client';

interface LoanData {
  id: number;
  loanId?: number;
  clientLoanId?: number;
  trackingNumber?: string | number;
  status?: string;
  loanStatus?: string;
}

const LoanUpdate: React.FC = () => {
  const navigate = useNavigate();
  const { loanId } = useParams<{ loanId: string }>();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loanData, setLoanData] = useState<LoanData | null>(null);
  const [modalOpen, setModalOpen] = useState(true);

  useEffect(() => {
    const fetchLoanData = async () => {
      if (!loanId) {
        setError('Loan ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Try to parse loanId
        const numericLoanId = parseInt(loanId, 10);
        if (isNaN(numericLoanId)) {
          setError('Invalid loan ID');
          setLoading(false);
          return;
        }

        // Fetch loan data from backend
        const response = await api.get(`/api/v1/client-loans/${numericLoanId}`);
        const data = response.data;

        // Validate that this loan can be updated (needs more info status)
        const status = data.status || data.loanStatus || '';
        const statusLower = status.toLowerCase();
        const needsMoreInfoStatuses = [
          'more_info_requested',
          'requesting for more info',
          'requesting more info',
          'additional info required',
          'pending additional information',
          'more info requested',
          'needs more information',
          'needs more info'
        ];

        if (status !== 'MORE_INFO_REQUESTED' && !needsMoreInfoStatuses.includes(statusLower)) {
          setError(`This loan cannot be updated. Current status: ${status}`);
          setLoading(false);
          return;
        }

        setLoanData({
          id: data.id || data.loanId || numericLoanId,
          loanId: data.loanId || data.id || numericLoanId,
          clientLoanId: data.clientLoanId || data.id || numericLoanId,
          trackingNumber: data.trackingNumber,
          status: status,
          loanStatus: data.loanStatus || status
        });

      } catch (err: any) {
        console.error('Error fetching loan data:', err);
        const errorMessage = err.response?.data?.message || 
                            err.response?.data?.error || 
                            'Failed to load loan information. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchLoanData();
  }, [loanId]);

  const handleClose = () => {
    setModalOpen(false);
    navigate('/loan-application'); // Navigate back to loan applications page
  };

  const handleSubmitSuccess = () => {
    setModalOpen(false);
    navigate('/loan-application'); // Navigate back after successful upload
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/loan-application')}
          >
            Back to Loan Applications
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!loanData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Loan data not found
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/loan-application')}
          >
            Back to Loan Applications
          </Button>
        </Paper>
      </Container>
    );
  }

  // Extract valid loan ID
  const validLoanId = loanData.id || loanData.loanId || loanData.clientLoanId || parseInt(loanId || '0', 10);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/loan-application')}
          sx={{ cursor: 'pointer' }}
        >
          Loan Applications
        </Link>
        <Typography color="text.primary">Update Loan Documents</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Update Loan Documents
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This loan requires additional information. Please upload the requested documents below.
        </Typography>
        {loanData.trackingNumber && (
          <Typography variant="body2" color="text.secondary">
            Tracking Number: <strong>{loanData.trackingNumber}</strong>
          </Typography>
        )}
      </Paper>

      {/* Standalone Document Upload Modal */}
      <LoanDocumentUploadModal
        open={modalOpen}
        onClose={handleClose}
        onSubmitSuccess={handleSubmitSuccess}
        loanId={validLoanId}
        loanNumber={loanData.trackingNumber?.toString()}
      />
    </Container>
  );
};

export default LoanUpdate;

